import { useState } from 'react'
import type { InvoiceEventFormValues } from '@klear/invoice/wire.client'
import {
  invoiceEventFormToCreateWire,
  invoiceEventFormToUpdateWire,
  validateInvoiceEventForm
} from '@klear/invoice/wire.client'
import { trpc, type RouterInputs } from '@klear/ui/lib/trpc'
import { InvoiceForm } from '@klear/order-event-ui/event-forms/invoice-form'
import { EventFormShell, type EventFormHandlerProps } from '@klear/order-event-ui/event-shared/event-form-shell'
import { useEventFormState } from '@klear/order-event-ui/event-shared/event-form-state'
import { useEventFormSave, type RunEventCommit } from '@klear/order-event-ui/event-shared/use-event-form-save'

export function InvoiceEventForm({ bootstrap, orderId, traderId, onSuccess, onCancel }: EventFormHandlerProps) {
  const formState = useEventFormState<InvoiceEventFormValues>({
    initial: bootstrap.formValues as InvoiceEventFormValues,
    validate: validateInvoiceEventForm
  })
  const [isLoading, setIsLoading] = useState(false)
  const { save } = useEventFormSave()
  const commit = trpc.invoice.commit.useMutation()
  const parentEventId = bootstrap.parentEventId

  const handleSubmit = async (data: InvoiceEventFormValues) => {
    setIsLoading(true)
    try {
      const runCommit: RunEventCommit = ({ id }) => {
        const input = (
          id == null
            ? { traderId, data: invoiceEventFormToCreateWire(data, orderId, parentEventId) }
            : { id, traderId, data: invoiceEventFormToUpdateWire(data, parentEventId) }
        ) as RouterInputs['invoice']['commit']
        return commit.mutateAsync(input)
      }
      const result = await save({
        traderId,
        eventId: bootstrap.editEventId,
        keptDocumentIds: (data.documents ?? []).map((document) => document.id),
        originalDocumentIds: bootstrap.originalDocumentIds,
        files: (data.files ?? []).filter((f): f is File => f instanceof File),
        runCommit
      })
      onSuccess?.(bootstrap.editEventId ?? result?.id)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventFormShell
      formState={formState}
      title={`${bootstrap.editEventId != null ? 'Edit' : 'Add'} Invoice`}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <InvoiceForm />
    </EventFormShell>
  )
}
